import React, { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";

/**
 * Real-loop ad slot — fetches an active campaign for the placement, fires an
 * impression on first paint, and tracks clicks before opening the destination.
 * Falls back to the static "Go Premium" upsell if no campaign is available.
 */
export function AdSlot({ variant = "inline", placement }) {
    const fallbackPlacement = placement || (variant === "sidebar" ? "sidebar" : "feed");
    const [ad, setAd] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const impressionFiredRef = useRef(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/ads/serve", { params: { placement: fallbackPlacement } });
                if (!cancelled) setAd(data?.ad || null);
            } catch {
                if (!cancelled) setAd(null);
            } finally {
                if (!cancelled) setLoaded(true);
            }
        })();
        return () => { cancelled = true; };
    }, [fallbackPlacement]);

    useEffect(() => {
        if (ad && !impressionFiredRef.current) {
            impressionFiredRef.current = true;
            api.post(`/ads/${ad.id}/impression`).catch(() => { /* ignore */ });
        }
    }, [ad]);

    const onClick = (e) => {
        if (!ad?.destination_url) return;
        api.post(`/ads/${ad.id}/click`).catch(() => { /* ignore */ });
        // Let the anchor open the URL natively; nothing to prevent.
        // Track impressions/clicks only — leave `e` alone.
    };

    if (!loaded) return null;

    if (!ad) return <FallbackAd variant={variant} />;

    if (variant === "sidebar") {
        return (
            <a
                data-testid="ad-sidebar"
                data-ad-id={ad.id}
                href={ad.destination_url || "#"}
                target="_blank"
                rel="noopener noreferrer sponsored"
                onClick={onClick}
                className="block bg-snowcap border border-ridge-line rounded-lg overflow-hidden hover:shadow-md transition-all group"
            >
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-gray px-4 pt-3">
                    Sponsored · {ad.advertiser || "RealWorld Outdoors"}
                </div>
                {ad.image_url ? (
                    <img src={ad.image_url} alt="" className="w-full aspect-[4/5] object-cover mt-2" />
                ) : (
                    <div className="aspect-[4/5] bg-alpine-mist mt-2 flex items-center justify-center text-xs text-slate-gray">
                        Ad
                    </div>
                )}
                <div className="p-4">
                    <div className="font-display font-bold text-midnight-pine text-sm leading-tight mb-1 group-hover:text-ember-orange transition-colors">
                        {ad.headline}
                    </div>
                    {ad.body && <div className="text-xs text-slate-gray mb-2 line-clamp-2">{ad.body}</div>}
                    <span className="inline-block px-3 py-1 rounded-md bg-midnight-pine text-white text-xs font-bold">
                        {ad.cta_label || "Learn more"}
                    </span>
                </div>
            </a>
        );
    }

    return (
        <a
            data-testid="ad-inline"
            data-ad-id={ad.id}
            href={ad.destination_url || "#"}
            target="_blank"
            rel="noopener noreferrer sponsored"
            onClick={onClick}
            className="block bg-snowcap border border-ridge-line rounded-lg p-5 flex items-center gap-4 hover:shadow-md hover:border-midnight-pine transition-all"
        >
            {ad.image_url ? (
                <img src={ad.image_url} alt="" className="w-20 h-20 rounded-md object-cover shrink-0" />
            ) : (
                <div className="w-20 h-20 bg-alpine-mist rounded-md border border-ridge-line shrink-0 flex items-center justify-center text-[10px] text-slate-gray">
                    Ad
                </div>
            )}
            <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-gray mb-1">
                    Sponsored · {ad.advertiser || "RealWorld Outdoors"}
                </div>
                <div className="font-display font-bold text-midnight-pine text-base leading-tight">
                    {ad.headline}
                </div>
                {ad.body && (
                    <div className="text-xs text-slate-gray mt-1 line-clamp-1">{ad.body}</div>
                )}
            </div>
            <span className="hidden sm:inline-block px-3 py-1.5 rounded-md bg-ember-orange text-white text-xs font-bold whitespace-nowrap">
                {ad.cta_label || "Learn more"}
            </span>
        </a>
    );
}

function FallbackAd({ variant }) {
    if (variant === "sidebar") {
        return (
            <div
                data-testid="ad-sidebar-fallback"
                className="bg-trail-tan/50 border border-dashed border-ridge-line rounded-lg p-4 text-center"
            >
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-gray mb-2">
                    Sponsored
                </div>
                <div className="font-display font-bold text-midnight-pine text-sm leading-tight mb-1">
                    Gear up for your next trail.
                </div>
                <div className="text-xs text-slate-gray mb-3">
                    Upgrade to Premium to remove ads.
                </div>
                <div className="aspect-[4/5] bg-alpine-mist rounded-md border border-ridge-line flex items-center justify-center text-xs text-slate-gray">
                    Ad 300×250
                </div>
            </div>
        );
    }
    return (
        <div
            data-testid="ad-inline-fallback"
            className="bg-trail-tan/40 border border-dashed border-ridge-line rounded-lg p-5 flex items-center justify-between gap-4"
        >
            <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-gray mb-1">
                    Sponsored
                </div>
                <div className="font-display font-bold text-midnight-pine text-base leading-tight">
                    Pack lighter, go further.
                </div>
                <div className="text-xs text-slate-gray mt-1">
                    Go Premium — no ads, full marketplace access.
                </div>
            </div>
            <div className="w-20 h-20 bg-alpine-mist rounded-md border border-ridge-line shrink-0 flex items-center justify-center text-[10px] text-slate-gray">
                Ad
            </div>
        </div>
    );
}

export default AdSlot;
